// Centralised, reusable configuration for the SINGLE simulated demo campaign
// scenario France -> Mauritius and its simulated report.
//
// SAFETY / SCOPE CONTRACT:
//   * 100% frontend, static config. NO network, NO DB write, NO backend.
//   * Everything is FICTIONAL and strictly for demonstration purposes.
//   * Visible ONLY for organisations in demo context (org.is_demo === true).
//   * Read-only: no create / edit / delete is ever wired here.
//   * All labels are bilingual (fr / en) so the narrative can evolve fast.

// Anchors used by the guided Demo journey (steps 5 & 6) to deep-link here.
export const DEMO_CAMPAIGN_ANCHOR = "demo-campaign";
export const DEMO_REPORT_ANCHOR = "demo-report";

// Mandatory simulation banner (verbatim spirit, bilingual).
export const SIMULATION_BANNER = {
  fr: "Simulation — résultats illustratifs, non issus d’une campagne réelle.",
  en: "Simulation — illustrative results, not from a real campaign.",
};

// Static UI strings for the two presentational cards.
export const DEMO_CAMPAIGN_UI = {
  fr: {
    cardTitle: "Campagne de démonstration",
    statusLabel: "Terminée",
    signalTitle: "Signal marché",
    objectiveTitle: "Objectif",
    audienceTitle: "Audience",
    periodTitle: "Période",
    channelsTitle: "Canaux",
    budgetTitle: "Budget simulé",
    partnersTitle: "Partenaires",
    reportCardTitle: "Rapport de performance (simulé)",
    resultsTitle: "Résultats simulés",
    reach: "Portée",
    impressions: "Impressions",
    clicks: "Clics",
    ctr: "CTR",
    leads: "Visites / leads",
    conversions: "Conversions",
    costPerResult: "Coût par résultat",
    progressTitle: "Progression vs objectif",
    progressLabel: "conversions sur un objectif de",
    learningsTitle: "Principaux enseignements",
    nextActionTitle: "Prochaine action recommandée",
    hypothesisTag: "Hypothèse",
  },
  en: {
    cardTitle: "Demonstration campaign",
    statusLabel: "Completed",
    signalTitle: "Market signal",
    objectiveTitle: "Objective",
    audienceTitle: "Audience",
    periodTitle: "Period",
    channelsTitle: "Channels",
    budgetTitle: "Simulated budget",
    partnersTitle: "Partners",
    reportCardTitle: "Performance report (simulated)",
    resultsTitle: "Simulated results",
    reach: "Reach",
    impressions: "Impressions",
    clicks: "Clicks",
    ctr: "CTR",
    leads: "Visits / leads",
    conversions: "Conversions",
    costPerResult: "Cost per result",
    progressTitle: "Progress vs objective",
    progressLabel: "conversions against a target of",
    learningsTitle: "Key learnings",
    nextActionTitle: "Recommended next action",
    hypothesisTag: "Assumption",
  },
};

// The single, fictional France -> Mauritius scenario. USD budget as required.
export const DEMO_CAMPAIGN_SCENARIO = {
  id: "demo-fr-mru-recapture",
  status: "Completed",
  currency: "USD",
  budget: 48000,
  name: {
    fr: "Reconquête France → Maurice",
    en: "France → Mauritius recapture",
  },
  signal: {
    fr: "Le marché émetteur France est en recul : arrivées −82,4 % entre les six derniers mois et les six mois précédents, avec un fléchissement marqué hors haute saison.",
    en: "The France source market is declining: arrivals −82.4% over the latest six months versus the previous six months, with a marked dip outside high season.",
  },
  objective: {
    fr: "Reconquérir la demande française et relancer les réservations sur les ailes de saison.",
    en: "Recapture French demand and revive bookings during shoulder seasons.",
  },
  audience: {
    fr: "Voyageurs français CSP+ 35–60 ans, urbains (Paris, Lyon, Bordeaux), sensibles au luxe balnéaire.",
    en: "French CSP+ travellers aged 35–60, urban (Paris, Lyon, Bordeaux), drawn to beach luxury.",
  },
  period: {
    fr: "Sept. – Nov. 2025 (aile de saison)",
    en: "Sep – Nov 2025 (shoulder season)",
  },
  channels: [
    { code: "paid_search", label: { fr: "Recherche payante", en: "Paid search" } },
    { code: "paid_social", label: { fr: "Réseaux sociaux payants", en: "Paid social" } },
    { code: "programmatic", label: { fr: "Display programmatique", en: "Programmatic display" } },
    { code: "influence", label: { fr: "Influence & créateurs", en: "Influence & creators" } },
  ],
  partners: [
    { name: "Air Mauritius", role: { fr: "Transport aérien", en: "Air carrier" } },
    { name: "Beachcomber Resorts", role: { fr: "Hébergement", en: "Accommodation" } },
    { name: "Atout Maurice", role: { fr: "Office de tourisme", en: "Tourism board" } },
  ],
};

// Simulated report figures. Coherent by construction (CTR = clicks / impressions,
// cost per result = budget / conversions). All fictional, non-observed.
export const DEMO_CAMPAIGN_REPORT = {
  objectiveTarget: 500, // conversions target
  metrics: {
    reach: 1180000,
    impressions: 3200000,
    clicks: 41600,
    ctr: 0.013, // 41600 / 3200000
    leads: 3450,
    conversions: 517,
    costPerResult: 92.8, // 48000 / 517
  },
  learnings: [
    {
      fr: "La recherche payante a généré 62 % des conversions à un coût par résultat inférieur à la moyenne.",
      en: "Paid search drove 62% of conversions at a below-average cost per result.",
    },
    {
      fr: "L’aile de saison (octobre) a surperformé : demande plus qualifiée et concurrence tarifaire moindre.",
      en: "The October shoulder period over-performed: more qualified demand and less price competition.",
    },
    {
      fr: "L’influence a soutenu la notoriété mais avec un taux de conversion plus faible que le search.",
      en: "Influence supported awareness but converted at a lower rate than search.",
    },
  ],
  nextAction: {
    fr: "Réallouer 15 % du budget vers la recherche payante et prolonger l’aile de saison sur décembre pour capter la demande de fin d’année.",
    en: "Reallocate 15% of budget toward paid search and extend the shoulder window into December to capture year-end demand.",
  },
};

// Localisation helper: returns node[lang] with a French fallback.
export function loc(node, lang) {
  if (node == null) return "";
  if (typeof node === "string" || typeof node === "number") return node;
  return node[lang] ?? node.fr ?? "";
}
